/**
 * applicantIdentity — the shared Identity_Set resolver + DocumentReference
 * reconciliation core (applicant-id-ssot-reconciliation spec, Phases 1–2).
 *
 * Applicant-owned data is keyed in TWO id spaces that were never linked:
 *   - `personId` — the deterministic anchor the NEW `/apply` stack writes under
 *     (`resolveCallerPersonId()` / `personIdContract.deriveCanonicalPersonId`).
 *   - legacy `Applicant.id` — the random UUID the LEGACY dashboard / documents /
 *     ServiceLog read under (email → `listApplicants` match).
 *
 * Surfaces used to hand-roll id resolution inline and pick the wrong space,
 * making a contestant's new-flow uploads invisible to legacy readers. This
 * module centralizes:
 *   1. `resolveApplicantIdentitySet` — the caller's `{ personId,
 *      legacyApplicantId }` (fail-soft partial set), and
 *   2. `mergeDocumentReferencesById` — the union-by-id reconciliation every
 *      document read composes, so a Dual_Read across both id spaces never
 *      double-counts a shared row and never drops a single-space contestant's
 *      rows.
 *
 * Pure + dependency-injected so it is unit-testable without AWS. The live hook
 * (`useApplicantIdentitySet`) wires the real client.
 */

/** The pair of ids that can anchor one authenticated applicant's data. */
export interface ApplicantIdentitySet {
  /** The durable personId the new `/apply` stack writes under; null when unauthenticated. */
  personId: string | null;
  /** The legacy Applicant.id the legacy surfaces read under; null when no Applicant record. */
  legacyApplicantId: string | null;
}

/** The minimal DocumentReference shape the reconciliation needs. */
export interface DocumentReferenceLite {
  id: string;
  documentType: string;
}

export interface ResolveApplicantIdentityDeps {
  /** Resolve the caller's personId from the authenticated session (or null). */
  resolvePersonId: () => Promise<string | null>;
  /** Resolve the caller's legacy Applicant.id by email (or null when none). */
  resolveLegacyApplicantId: () => Promise<string | null>;
}

/**
 * Resolve the caller's {@link ApplicantIdentitySet}. Fail-soft: each id resolves
 * independently and a failure/absence yields `null` for that id rather than
 * throwing, so reads degrade to whichever id is available (Req 1.1, 1.3).
 */
export async function resolveApplicantIdentitySet(
  deps: ResolveApplicantIdentityDeps,
): Promise<ApplicantIdentitySet> {
  const [personId, legacyApplicantId] = await Promise.all([
    deps.resolvePersonId().catch(() => null),
    deps.resolveLegacyApplicantId().catch(() => null),
  ]);
  return { personId, legacyApplicantId };
}

/**
 * The distinct, non-empty ids to Dual_Read across for an Identity_Set. Collapses
 * duplicates (when the two spaces coincide) so a read issues at most one query
 * per real id (Req 2.1).
 */
export function distinctApplicantIds(ids: ApplicantIdentitySet): string[] {
  const out: string[] = [];
  for (const id of [ids.personId, ids.legacyApplicantId]) {
    if (id != null && id.trim() !== '' && !out.includes(id)) out.push(id);
  }
  return out;
}

/**
 * Union DocumentReferences from multiple id-space reads by `id`, so a Dual_Read
 * across `personId` + `legacyApplicantId` returns each distinct document exactly
 * once (Req 2.1, 2.4). Last-writer-wins on the (id → documentType) mapping, which
 * is irrelevant since the same `id` always carries the same type.
 */
export function mergeDocumentReferencesById<T extends { id: string }>(
  ...lists: ReadonlyArray<ReadonlyArray<T>>
): T[] {
  const byId = new Map<string, T>();
  for (const list of lists) {
    for (const doc of list) {
      if (doc && typeof doc.id === 'string') byId.set(doc.id, doc);
    }
  }
  return [...byId.values()];
}

/**
 * Dual_Read a contestant's DocumentReferences across every distinct id in the
 * Identity_Set and union by id. Issues one `listByApplicantId` per distinct id
 * (at most two), tolerates a per-id failure (that id contributes nothing rather
 * than failing the whole read), and returns the merged list (Req 2.1, 2.4).
 */
export async function fetchAllDocumentReferences<T extends { id: string }>(
  ids: ApplicantIdentitySet,
  competitionYear: number,
  deps: {
    listByApplicantId: (applicantId: string, competitionYear: number) => Promise<T[]>;
  },
): Promise<T[]> {
  const idList = distinctApplicantIds(ids);
  if (idList.length === 0) return [];
  const results = await Promise.all(
    idList.map((id) => deps.listByApplicantId(id, competitionYear).catch(() => [] as T[])),
  );
  return mergeDocumentReferencesById(...results);
}
